import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack,
  InputAdornment, CircularProgress, ToggleButtonGroup, ToggleButton,
  Select, MenuItem, FormControl,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { loginWithEmail, sendOtp, verifyOtp } from '../api/endpoints/auth';
import { validatePhone } from '../utils/validators';
import countryCodes from '../utils/countryCodes';

const Login = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useContext(AuthContext);
  const [authType, setAuthType] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const handleEmailLogin = async () => {
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setLoading(true);
    try {
      const { data } = await loginWithEmail(email, password);
      const userData = data.data.user;
      if (userData.role !== 'admin') {
        toast.error('Access denied. Admin account required.');
        return;
      }
      authLogin(data.data.token, userData);
      toast.success('Login successful');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const fullPhone = countryCode + phone;
    const err = validatePhone(fullPhone);
    if (err) { setPhoneError(err); return; }
    setPhoneError('');
    setLoading(true);
    try {
      const { data } = await sendOtp(fullPhone);
      localStorage.setItem('otpId', data.data.otpId);
      setStep(2);
      toast.success('OTP sent to your phone');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) { toast.error('Please enter a valid OTP'); return; }
    setLoading(true);
    try {
      const { data } = await verifyOtp(localStorage.getItem('otpId'), otp);
      const userData = data.data.user;
      if (userData.role !== 'admin') {
        toast.error('Access denied. Admin account required.');
        return;
      }
      authLogin(data.data.token, userData);
      toast.success('Login successful');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #2D1B69 0%, #1A0F3D 100%)', p: 2 }}>
      <Card sx={{ maxWidth: 440, width: '100%', borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AutoAwesomeIcon sx={{ fontSize: 48, color: '#FFD700', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: '#2D1B69' }}>
              AstroPandit Admin
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sign in to your admin account
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={authType}
            exclusive
            onChange={(e, val) => { if (val) { setAuthType(val); setStep(1); setOtp(''); } }}
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton value="email" sx={{ textTransform: 'none', fontWeight: 600 }}>
              <EmailIcon sx={{ mr: 1, fontSize: 18 }} /> Email
            </ToggleButton>
            <ToggleButton value="phone" sx={{ textTransform: 'none', fontWeight: 600 }}>
              <PhoneIcon sx={{ mr: 1, fontSize: 18 }} /> Phone & OTP
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack spacing={2.5}>
            {authType === 'email' ? (
              <>
                <TextField label="Email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="primary" /></InputAdornment> }}
                  placeholder="admin@astropandit.com" />
                <TextField label="Password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="primary" /></InputAdornment> }}
                  placeholder="Enter your password" />
                <Button variant="contained" size="large" fullWidth onClick={handleEmailLogin}
                  disabled={loading} sx={{ py: 1.3 }}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </>
            ) : (
              step === 1 ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <FormControl size="small" sx={{ minWidth: 110, mt: 0.5 }}>
                      <Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                        {countryCodes.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="Phone Number" value={phone}
                      onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setPhoneError(''); }}
                      error={!!phoneError} helperText={phoneError} fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon color="primary" /></InputAdornment> }}
                      placeholder="9876543210" />
                  </Stack>
                  <Button variant="contained" size="large" fullWidth onClick={handleSendOtp}
                    disabled={loading} sx={{ py: 1.3 }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
                  </Button>
                </>
              ) : (
                <>
                  <TextField label="Enter OTP" value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="primary" /></InputAdornment> }}
                    placeholder="Enter the OTP sent to your phone" />
                  <Button variant="contained" size="large" fullWidth onClick={handleVerifyOtp}
                    disabled={loading} sx={{ py: 1.3 }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
                  </Button>
                  <Button variant="text" size="small" onClick={() => { setStep(1); setOtp(''); }}
                    sx={{ alignSelf: 'center' }}>
                    Change phone number
                  </Button>
                </>
              )
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
